import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, Heart, MessageCircle, Smile } from "lucide-react";
import { api } from "@/lib/api";

const timeAgo = (iso) => {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
};

function line(n) {
  const who = n.actor_name || "A sister";
  if (n.kind === "like") return { icon: Heart, text: `${who} liked your post` };
  if (n.kind === "react") return { icon: Smile, text: `${who} reacted ${n.emoji} to your post` };
  if (n.kind === "reply") return { icon: MessageCircle, text: `${who} replied to your post` };
  if (n.kind === "comment_react") return { icon: Smile, text: `${who} reacted ${n.emoji} to your comment` };
  return { icon: Bell, text: `${who} interacted with you` };
}

export default function Notifications() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/notifications");
        setItems(data.notifications || []);
        if ((data.unread_count || 0) > 0) {
          await api.post("/notifications/read-all");
          window.dispatchEvent(new Event("notif-read"));
        }
      } finally { setLoading(false); }
    })();
  }, []);

  return (
    <div className="space-y-6 max-w-2xl mx-auto" data-testid="notifications-page">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-[#C89B55] font-semibold">Sisterly</p>
        <h1 className="font-heading text-3xl lg:text-4xl font-semibold mt-1">Notifications</h1>
        <p className="text-[#59535B] mt-2">When sisters react to or reply to your posts, you'll see it here.</p>
      </div>

      {loading ? (
        <p className="text-sm text-[#8A838E]">Loading…</p>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center p-12 text-center bg-white rounded-3xl border border-[#291B2D]/5" data-testid="notifications-empty">
          <span className="w-14 h-14 rounded-full bg-[#F3EEF4] text-[#7B4C74] flex items-center justify-center mb-3"><Bell className="h-6 w-6" /></span>
          <p className="font-heading text-lg">All caught up</p>
          <p className="text-sm text-[#8A838E] mt-1 max-w-sm">Share in a Circle and the love will start rolling in.</p>
        </div>
      ) : (
        <div className="space-y-2">
          {items.map((n, i) => {
            const { icon: Icon, text } = line(n);
            return (
              <motion.button key={n.notif_id} data-testid={`notification-item-${n.notif_id}`}
                initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25, delay: i * 0.02 }}
                onClick={() => navigate("/community")}
                className={`w-full text-left flex items-center gap-3 rounded-2xl p-4 border transition-smooth hover:-translate-y-0.5 ${n.read ? "bg-white border-[#291B2D]/5" : "bg-[#F3EEF4] border-[#7B4C74]/20"}`}>
                <span className="h-9 w-9 shrink-0 rounded-full bg-[#F7F2EA] text-[#7B4C74] flex items-center justify-center"><Icon className="h-4 w-4" /></span>
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-[#252326]">{text}</p>
                  {n.snippet && <p className="text-xs text-[#8A838E] truncate mt-0.5">"{n.snippet}"</p>}
                </div>
                <span className="text-xs text-[#8A838E] shrink-0">{timeAgo(n.created_at)}</span>
              </motion.button>
            );
          })}
        </div>
      )}
    </div>
  );
}
